import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { Check, Loader2 } from "lucide-react";

import { useChats } from "~/entities/chat/useChats";
import { useSearchChats, SEARCH_MIN_CHARS } from "~/entities/chat";
import type { Chat } from "~/entities/chat";
import { Button } from "~/shared/ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "~/shared/ui/dialog";
import { Input } from "~/shared/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "~/shared/ui/tabs";
import { cn } from "~/shared/lib/utils";

import { useMoveMessages } from "./useMoveMessages";

type Mode = "existing" | "new";

interface MoveMessagesDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    sourceChatId: string;
    workspaceId: string;
    messageIds: string[];
    // true, когда выбраны ВСЕ сообщения источника → после переноса он опустеет.
    willEmptySource: boolean;
    // Успех — очищаем выбор в родителе и закрываем панель.
    onMoved: () => void;
}

export function MoveMessagesDialog({
    open,
    onOpenChange,
    sourceChatId,
    workspaceId,
    messageIds,
    willEmptySource,
    onMoved,
}: MoveMessagesDialogProps) {
    const { t } = useTranslation("chat");
    const navigate = useNavigate();
    const move = useMoveMessages();

    const [mode, setMode] = useState<Mode>("existing");
    const [query, setQuery] = useState("");
    const [targetChatId, setTargetChatId] = useState<string | null>(null);
    const [newTitle, setNewTitle] = useState("");
    // Второе окно: подтверждение удаления опустевшего источника.
    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

    // Каждое открытие — с чистого листа.
    useEffect(() => {
        if (open) {
            setMode("existing");
            setQuery("");
            setTargetChatId(null);
            setNewTitle("");
            setConfirmDeleteOpen(false);
            move.reset();
        }
        // move — стабильный mutation-объект, намеренно не в deps.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const trimmedQuery = query.trim();
    const isSearchActive = trimmedQuery.length >= SEARCH_MIN_CHARS;
    const recentQuery = useChats(workspaceId);
    const searchQuery = useSearchChats(query, workspaceId);

    // Целевой чат не может быть исходным — его прячем из списка.
    const chats = useMemo<Chat[]>(() => {
        const source = isSearchActive ? (searchQuery.data?.chats ?? []) : (recentQuery.data?.chats ?? []);
        return source.filter((c) => c.id !== sourceChatId);
    }, [isSearchActive, searchQuery.data, recentQuery.data, sourceChatId]);

    const isFetching = isSearchActive && searchQuery.isFetching;
    const showTooShort = trimmedQuery.length > 0 && trimmedQuery.length < SEARCH_MIN_CHARS;
    const trimmedTitle = newTitle.trim();

    const canSubmit =
        !move.isPending &&
        messageIds.length > 0 &&
        (mode === "existing" ? !!targetChatId : trimmedTitle.length > 0);

    const performMove = async (deleteSourceIfEmpty: boolean) => {
        try {
            const res = await move.mutateAsync({
                sourceChatId,
                workspaceId,
                messageIds,
                targetChatId: mode === "existing" ? (targetChatId ?? undefined) : undefined,
                newChatTitle: mode === "new" ? trimmedTitle : undefined,
                deleteSourceIfEmpty,
            });
            toast.success(t("moveMessages.dialog.successMoved", { count: res.moved }));
            onMoved();
            setConfirmDeleteOpen(false);
            onOpenChange(false);
            // Источник удалён (мы на его странице) → уходим в целевой чат.
            if (res.sourceDeleted) void navigate(`/chat/${res.targetChatId}`);
        } catch (error) {
            const fallback = t("moveMessages.dialog.errorGeneric");
            toast.error(error instanceof Error && error.message ? error.message : fallback);
        }
    };

    const handleSubmit = async () => {
        if (!canSubmit) return;
        // Все сообщения уходят → сначала спрашиваем про удаление пустого чата.
        if (willEmptySource) {
            setConfirmDeleteOpen(true);
            return;
        }
        await performMove(false);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>{t("moveMessages.dialog.title")}</DialogTitle>
                    <DialogDescription>
                        {t("moveMessages.dialog.description", { count: messageIds.length })}
                    </DialogDescription>
                </DialogHeader>

                <Tabs value={mode} onValueChange={(v) => setMode(v as Mode)}>
                    <TabsList className="grid w-full grid-cols-2">
                        <TabsTrigger value="existing">{t("moveMessages.dialog.tabExisting")}</TabsTrigger>
                        <TabsTrigger value="new">{t("moveMessages.dialog.tabNew")}</TabsTrigger>
                    </TabsList>

                    <TabsContent value="existing" className="mt-3 flex h-64 flex-none flex-col gap-2">
                        <Input
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder={t("moveMessages.dialog.searchPlaceholder")}
                        />
                        <div className="flex-1 overflow-y-auto rounded-md border border-white/10">
                            {isFetching && chats.length === 0 ? (
                                <div className="flex items-center justify-center gap-2 py-8 text-xs text-zinc-500">
                                    <Loader2 className="size-3.5 animate-spin" />
                                    {t("moveMessages.dialog.searching")}
                                </div>
                            ) : chats.length === 0 ? (
                                <div className="py-8 text-center text-xs text-zinc-500">
                                    {showTooShort
                                        ? t("moveMessages.dialog.minChars", { count: SEARCH_MIN_CHARS })
                                        : t("moveMessages.dialog.empty")}
                                </div>
                            ) : (
                                <ul className="p-1">
                                    {chats.map((chat) => {
                                        const active = chat.id === targetChatId;
                                        return (
                                            <li key={chat.id}>
                                                <button
                                                    type="button"
                                                    onClick={() => setTargetChatId(chat.id)}
                                                    className={cn(
                                                        "flex w-full items-center gap-2 rounded px-2 py-2 text-left text-sm",
                                                        active
                                                            ? "bg-primary/15 text-primary"
                                                            : "text-zinc-300 hover:bg-white/5",
                                                    )}
                                                >
                                                    <span className="truncate">
                                                        {chat.title ?? t("untitledChat")}
                                                    </span>
                                                    {active && <Check className="ml-auto size-4 shrink-0" />}
                                                </button>
                                            </li>
                                        );
                                    })}
                                </ul>
                            )}
                        </div>
                    </TabsContent>

                    <TabsContent value="new" className="mt-3 flex h-64 flex-none flex-col gap-2">
                        <Input
                            value={newTitle}
                            onChange={(e) => setNewTitle(e.target.value)}
                            placeholder={t("moveMessages.dialog.newTitlePlaceholder")}
                            maxLength={200}
                        />
                    </TabsContent>
                </Tabs>

                <DialogFooter>
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        disabled={move.isPending}
                    >
                        {t("moveMessages.dialog.cancel")}
                    </Button>
                    <Button type="button" variant="accent" onClick={handleSubmit} disabled={!canSubmit}>
                        {move.isPending ? t("moveMessages.dialog.submitting") : t("moveMessages.dialog.move")}
                    </Button>
                </DialogFooter>
            </DialogContent>

            {/* Второе окно: источник опустеет — предложить удалить пустой чат.
                Подтверждение → перенос с удалением; отмена → возврат к переносу.
                Вложенный Radix-Dialog (не самописный overlay) — корректный
                стекинг и клики поверх модалки переноса. */}
            <Dialog open={confirmDeleteOpen} onOpenChange={setConfirmDeleteOpen}>
                <DialogContent className="sm:max-w-sm">
                    <DialogHeader>
                        <DialogTitle>{t("moveMessages.deleteEmpty.title")}</DialogTitle>
                        <DialogDescription>{t("moveMessages.deleteEmpty.description")}</DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => setConfirmDeleteOpen(false)}
                            disabled={move.isPending}
                        >
                            {t("moveMessages.deleteEmpty.cancel")}
                        </Button>
                        <Button
                            type="button"
                            variant="destructive"
                            onClick={() => performMove(true)}
                            disabled={move.isPending}
                        >
                            {t("moveMessages.deleteEmpty.confirm")}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Dialog>
    );
}
